import { now, parseAbsoluteToLocal } from "@internationalized/date"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { ReceiptMini } from "ipctg-inflow-types/InFlowDB"
import {
  CancellationSource,
  ConvenienceFeeVerbiage,
  ReceiptType,
} from "ipctg-inflow-types/InFlowEnums"
import { useTranslation } from "react-i18next"

import { Button } from "@/globalComponents/atoms/Button"
import Heading from "@/globalComponents/atoms/Heading"
import Icon from "@/globalComponents/atoms/Icon"
import Separator from "@/globalComponents/atoms/Separator"
import DoorAccessCodeCard from "@/globalComponents/molecules/DoorAccessCodeCard"
import {
  formatDateForDisplay,
  formatZonedDateTimeWithTimeZone,
  getLocale,
} from "@/utilities/etc"
import { receiptQueryOptions } from "@/utilities/hooks/queries/shared"
import { meQueryOptions } from "@/utilities/hooks/queries/user"
import useToggle from "@/utilities/hooks/useToggle"
import {
  normalizedAPILicensePlate,
  prettyCurrencyNumber,
} from "@/utilities/normalizers"

import PlateUpdateModal from "./-components/PlateUpdateModal"
import SendReceiptModal from "./-components/SendReceiptModal"

export const Route = createFileRoute("/receipt/$receiptID")({
  beforeLoad: ({ context }) => {
    context.queryClient.clear()
  },
  loader: ({ context }) => {
    context.setDocumentThemeMode("root")
  },
  component: () => <RouteComponent />,
})

function RouteComponent() {
  const { receiptID } = Route.useParams()
  return <ReceiptRouteComponent receiptID={receiptID} />
}

export function ReceiptRouteComponent({ receiptID }: { receiptID: string }) {
  const { t, i18n } = useTranslation("receipt")
  const { data, refetch } = useSuspenseQuery(receiptQueryOptions({ receiptID }))
  const { data: me } = useSuspenseQuery(meQueryOptions())
  const receipt = data.receipt
  const [sendPhoneReceiptModalOpen, togglePhoneSendReceiptModal] =
    useToggle(false)
  const [sendEmailReceiptModalOpen, toggleEmailSendReceiptModal] =
    useToggle(false)
  const [plateUpdateModalOpen, togglePlateUpdateModal] = useToggle(false)

  /*
    When the user is logged in, pre-fill the send-receipt fields with their
    Cognito contact info so they don't have to retype it. Falls back to an
    empty field for logged-out users or when the attribute isn't set.
    The phone field feeds TextField.PhoneWithCountrySelect, which expects the
    value to include the dial code, so pass the raw E.164 Cognito value and
    let the component detect the country and format it for display.
  */
  const phoneDefault = me?.cognitoUser.phone_number || ""
  const emailDefault = me?.cognitoUser.email || ""

  const isReserveReceipt = receipt.receiptType === ReceiptType.Reservation
  const isCancelledReservation = receipt.receiptType === ReceiptType.Refund
  const cancelledByVenue = receipt.cancelledBy === CancellationSource.Admin

  // Check if plate switching is allowed
  const canUpdatePlate = !receipt.blockPlateSwitch
  const plateSwitchDeadline = receipt.allowPlateSwitchUntil
    ? parseAbsoluteToLocal(receipt.allowPlateSwitchUntil)
    : null
  const isBeforeDeadline = plateSwitchDeadline
    ? now(plateSwitchDeadline.timeZone).compare(plateSwitchDeadline) < 0
    : false
  const showPlateUpdateButton = canUpdatePlate && isBeforeDeadline
  const showVehicleSection =
    !isReserveReceipt && !isCancelledReservation && !!receipt.plate

  return (
    <>
      <Heading level={3} as={1} className="center">
        {isReserveReceipt || isCancelledReservation
          ? receipt.bookingCalendarName
          : formatDateForDisplay({
              isoDateString: receipt.endTime,
              locale: i18n.resolvedLanguage || getLocale(),
            })}
      </Heading>
      {receipt.accessCode ? (
        <DoorAccessCodeCard>{receipt.accessCode}</DoorAccessCodeCard>
      ) : (
        <></>
      )}
      <Heading level={4} as={2}>
        {t("sessionDetails.header")}
      </Heading>
      <dl>
        <div>
          <dt>{t("sessionDetails.list.location")}</dt>
          <dd>{receipt.locationName}</dd>
        </div>
        {isReserveReceipt || isCancelledReservation ? (
          <div>
            <dt>{t("sessionDetails.list.dateOfSale")}</dt>
            <dd>
              {formatDateForDisplay({
                isoDateString: receipt.createdAtTime || receipt.startTime,
                locale: i18n.resolvedLanguage || getLocale(),
              })}
            </dd>
          </div>
        ) : (
          <div>
            <dt>{t("sessionDetails.list.date")}</dt>
            <dd>
              {formatDateForDisplay({
                isoDateString: receipt.endTime,
                locale: i18n.resolvedLanguage || getLocale(),
              })}
            </dd>
          </div>
        )}
      </dl>
      <Separator orientation="horizontal" />

      {/* Non-reserve receipts: separate vehicle section */}
      {showVehicleSection && (
        <>
          <Heading level={4} as={2}>
            {t("vehicle.header")}
          </Heading>
          <dl>
            <div>
              <dt>{t("vehicle.list.licensePlate")}</dt>
              <dd>{normalizedAPILicensePlate(receipt.plate)}</dd>
            </div>
          </dl>
          {showPlateUpdateButton && (
            <Button buttonType="primary" onPress={togglePlateUpdateModal}>
              <Icon name="car" slot="left" /> {t("incorrectLicensePlate")}
            </Button>
          )}
          <Separator orientation="horizontal" />
        </>
      )}

      {/* Reserve receipts: parking info */}
      {(isReserveReceipt || isCancelledReservation) && (
        <>
          <Heading level={4} as={2}>
            {t("parkingInformation.header")}
          </Heading>
          <dl>
            {!!receipt.rateName && (
              <div>
                <dt>{t("parkingInformation.list.rate")}</dt>
                <dd>{receipt.rateName}</dd>
              </div>
            )}
            <div>
              <dt>{t("parkingInformation.list.startTime")}</dt>
              <dd>
                {formatZonedDateTimeWithTimeZone(
                  parseAbsoluteToLocal(receipt.startTime)
                )}
              </dd>
            </div>
            <div>
              <dt>{t("parkingInformation.list.endTime")}</dt>
              <dd>
                {formatZonedDateTimeWithTimeZone(
                  parseAbsoluteToLocal(receipt.endTime)
                )}
              </dd>
            </div>
            {!!receipt.plate && (
              <div>
                <dt>{t("parkingInformation.list.licensePlate")}</dt>
                <dd>{normalizedAPILicensePlate(receipt.plate)}</dd>
              </div>
            )}
          </dl>
          {showPlateUpdateButton && (
            <Button buttonType="primary" onPress={togglePlateUpdateModal}>
              <Icon name="car" slot="left" /> {t("incorrectLicensePlate")}
            </Button>
          )}
          <Separator orientation="horizontal" />
        </>
      )}

      {/* Non-reserve: existing receipt-type section (lot/times or flex pass) */}
      {!isReserveReceipt && !isCancelledReservation && (
        <>
          <ReceiptTypeSection receipt={data.receipt} />
          <Separator orientation="horizontal" />
        </>
      )}

      <Heading level={4} as={2}>
        {t("paymentDetails.header")}
      </Heading>
      <dl>
        <div>
          <dt>{t("paymentDetails.list.invoiceNumber")}</dt>
          <dd>{receipt.invoiceID}</dd>
        </div>
        <div>
          <dt>{t("paymentDetails.list.paymentMethod")}</dt>
          <dd>
            {receipt.paymentDescription?.toLowerCase() === "flex pass"
              ? "Flex Park"
              : receipt.paymentDescription}
          </dd>
        </div>
        {!!receipt.baseRate && (
          <div>
            <dt>{t("paymentDetails.list.baseRate")}</dt>
            <dd>{prettyCurrencyNumber(Math.abs(receipt.baseRate))}</dd>
          </div>
        )}
        {!!receipt.discount && (
          <div style={{ color: "var(--success-10)" }}>
            <dt>
              {receipt.promotionUserFacingRedeemCopy
                ? t("paymentDetails.list.promotion", {
                    copy: receipt.promotionUserFacingRedeemCopy,
                  })
                : t("paymentDetails.list.discount")}
            </dt>
            <dd style={{ fontWeight: 500 }}>
              -{prettyCurrencyNumber(Math.abs(receipt.discount))}
            </dd>
          </div>
        )}
        {!!receipt.convenienceFee && (
          <div>
            <dt>
              {t("paymentDetails.list.convenienceFee", {
                convenienceFeeVerbiage: getConvenienceFeeVerbiage(),
              })}
            </dt>
            <dd>{prettyCurrencyNumber(Math.abs(receipt.convenienceFee))}</dd>
          </div>
        )}
        {!!receipt.tax && (
          <div>
            <dt>{t("paymentDetails.list.tax")}</dt>
            <dd>{prettyCurrencyNumber(Math.abs(receipt.tax))}</dd>
          </div>
        )}
        <div>
          <dt>{t("paymentDetails.list.totalPaid")}</dt>
          <dd>{prettyCurrencyNumber(Math.abs(receipt.amountPaid))}</dd>
        </div>
      </dl>

      {/* Refund Details — cancelled reservations only */}
      {isCancelledReservation && (
        <div className="refund-details">
          <Separator orientation="horizontal" />
          <Heading level={4} as={2}>
            {t("refundDetails.header")}
          </Heading>
          {cancelledByVenue && (
            <p style={{ fontStyle: "italic" }}>
              {t("refundDetails.cancelledByVenue")}
            </p>
          )}
          <dl>
            <div style={{ color: "var(--success-10)" }}>
              <dt>{t("refundDetails.list.amountRefunded")}</dt>
              <dd style={{ fontWeight: 500 }}>
                {prettyCurrencyNumber(receipt.amountPaid)}
              </dd>
            </div>
          </dl>
        </div>
      )}

      <Button buttonType="primary" onPress={togglePhoneSendReceiptModal}>
        <Icon name="speechBubble" slot="left" /> {t("sendReceiptViaText")}
      </Button>
      <Button buttonType="primary" onPress={toggleEmailSendReceiptModal}>
        <Icon name="mail" slot="left" /> {t("sendReceiptViaEmail")}
      </Button>
      <SendReceiptModal
        isOpen={sendPhoneReceiptModalOpen}
        onOpenChange={togglePhoneSendReceiptModal}
        receiptID={receiptID}
        method="phone"
        defaultValue={phoneDefault}
      />
      <SendReceiptModal
        isOpen={sendEmailReceiptModalOpen}
        onOpenChange={toggleEmailSendReceiptModal}
        receiptID={receiptID}
        method="email"
        defaultValue={emailDefault}
      />
      <PlateUpdateModal
        isOpen={plateUpdateModalOpen}
        onOpenChange={togglePlateUpdateModal}
        receiptID={receiptID}
        currentPlate={receipt.plate}
        onSuccess={() => refetch()}
      />
    </>
  )

  function getConvenienceFeeVerbiage() {
    switch (receipt.convenienceFeeVerbiage) {
      case ConvenienceFeeVerbiage.ConvenienceFee:
        return t("paymentDetails.convenienceFeeVerbiage.convenienceFee")
      case ConvenienceFeeVerbiage.ServiceFee:
        return t("paymentDetails.convenienceFeeVerbiage.serviceFee")
      default:
        return t("paymentDetails.convenienceFeeVerbiage.unknown")
    }
  }
}

/**
 * Renders receipt-specific session/pass details based on the ReceiptType.
 *
 * Different purchase flows produce different receipt types, and each type
 * requires displaying contextually relevant information to the user:
 *
 * - **FlexPassPurchase**: The user bought a flex parking pass (a reusable pass
 *   that can be redeemed across multiple sessions). We show the pass type and
 *   purchase time since there is no fixed parking session with start/end times.
 *
 * - **Default**: All other receipt types fall through to a generic session view
 *   showing lot name, start time, and end time. The ReceiptType enum also
 *   includes Flex, Monthly, OnetimePayment, Extension, and PrepaidEventTicket,
 *   but those are not explicitly handled here — if any of them need unique
 *   display logic in the future, add a new case to the switch.
 */
function ReceiptTypeSection({ receipt }: { receipt: ReceiptMini }) {
  const { t } = useTranslation("receipt")

  switch (receipt.receiptType) {
    case ReceiptType.FlexPassPurchase:
      return (
        <>
          <Heading level={4} as={2}>
            {t("parkingPassInformation.header")}
          </Heading>
          <dl>
            <div>
              <dt>{t("parkingPassInformation.list.passType")}</dt>
              <dd>{t("parkingPassInformation.list.flexParkPurchase")}</dd>
            </div>
            <div>
              <dt>{t("parkingPassInformation.list.purchaseTime")}</dt>
              <dd>
                {formatZonedDateTimeWithTimeZone(
                  parseAbsoluteToLocal(receipt.startTime)
                )}
              </dd>
            </div>
          </dl>
        </>
      )
    default:
      return (
        <>
          <Heading level={4} as={2}>
            {t("sessionInformation.header")}
          </Heading>
          <dl>
            <div>
              <dt>{t("sessionInformation.list.lot")}</dt>
              <dd>{receipt.lotName}</dd>
            </div>
            <div>
              <dt>{t("sessionInformation.list.startTime")}</dt>
              <dd>
                {formatZonedDateTimeWithTimeZone(
                  parseAbsoluteToLocal(receipt.startTime)
                )}
              </dd>
            </div>
            <div>
              <dt>{t("sessionInformation.list.endTime")}</dt>
              <dd>
                {formatZonedDateTimeWithTimeZone(
                  parseAbsoluteToLocal(receipt.endTime)
                )}
              </dd>
            </div>
          </dl>
        </>
      )
  }
}
